import type { FmMapWorkspaceDescriptor } from "./FmMapWorkspaceRoot.types";
import { MAP_WORKSPACE_STATES } from "./FmMapWorkspaceRoot.types";
import { validateMapWorkspacePanels } from "./FmMapWorkspacePanel.validation";
import { validateMapWorkspaceInspection } from "./FmMapWorkspaceInspection.validation";
import { validateMapWorkspaceShape } from "./FmMapWorkspaceRoot.shape";
import { validateMapWorkspaceSelection } from "./FmMapWorkspaceSelection";
import {
  requireMapWorkspaceText as requireText,
  requireMapWorkspaceTextArray as requireTextArray,
  uniqueMapWorkspaceIds as uniqueIds,
} from "./FmMapWorkspaceValidation.shared";
import {
  MAP_WORKSPACE_MOBILE_ORDER,
  MAP_WORKSPACE_MODE_IDENTITY,
  MAP_WORKSPACE_STATE_POLICY,
} from "./mapWorkspaceRecipes";
import { FM_MAP_LAYER_CAPABILITIES } from "./FmMapLayerCapabilities";
import { validateAnalysisWorkspaceDescriptor } from "./FmAnalysisWorkspaceRoot.validation";
import { assertFmFormFlowPageDescriptor } from "./FmFormFlowPage.validation";
import { assertFmOperationalWorkspaceShell } from "./FmOperationalWorkspaceRoot.validation";
import { hasMapSlot } from "@/shared/config/mapSlotRegistry";
function validateStory(descriptor: FmMapWorkspaceDescriptor): void {
  const { story } = descriptor;
  requireText(story.ownerQuestion, "story.ownerQuestion");
  requireText(story.literalAnswer, "story.literalAnswer");
  requireTextArray(story.requiredServedFields, "story.requiredServedFields");
  requireTextArray(story.requiredProvenance, "story.requiredProvenance");
  if (story.dominantBeat !== "map") {
    throw new Error(
      'Invalid map workspace descriptor: story.dominantBeat must be "map".',
    );
  }
  const identity = MAP_WORKSPACE_MODE_IDENTITY[descriptor.mode];
  if (
    story.conversionRole !== identity.conversionRole ||
    story.allowedTransitions.length !== identity.allowedTransitions.length ||
    story.allowedTransitions.some(
      (mode, index) => mode !== identity.allowedTransitions[index],
    )
  ) {
    throw new Error(
      "Invalid map workspace descriptor: story transition graph and conversion role must match the active mode.",
    );
  }
  if (
    story.mobileOrder.length !== MAP_WORKSPACE_MOBILE_ORDER.length ||
    story.mobileOrder.some(
      (beat, index) => beat !== MAP_WORKSPACE_MOBILE_ORDER[index],
    )
  ) {
    throw new Error(
      "Invalid map workspace descriptor: story.mobileOrder must preserve the canonical map workspace order.",
    );
  }
  MAP_WORKSPACE_STATES.forEach((state) => {
    const behavior = story.stateBehavior[state];
    requireText(behavior.answer, `story.stateBehavior.${state}.answer`);
    if (typeof behavior.retainsServedAnswer !== "boolean") {
      throw new Error(
        `Invalid map workspace descriptor: story.stateBehavior.${state} flags must be boolean.`,
      );
    }
    const policy = MAP_WORKSPACE_STATE_POLICY[state];
    if (
      behavior.retainsServedAnswer !== policy.retainsServedAnswer ||
      (policy.retainsServedAnswer && behavior.answer !== story.literalAnswer)
    ) {
      throw new Error(
        `Invalid map workspace descriptor: story.stateBehavior.${state} must match the canonical state policy.`,
      );
    }
  });
}

function isNullableObject(value: unknown): boolean {
  return value === null || (typeof value === "object" && !Array.isArray(value));
}

type MapWorkspaceData = NonNullable<FmMapWorkspaceDescriptor["mapData"]>;

function validateMapDataTypes(data: MapWorkspaceData): void {
  if (
    !isNullableObject(data.propertyForestContext) ||
    !isNullableObject(data.riskOverlay) ||
    !isNullableObject(data.restoredSelection) ||
    !isNullableObject(data.restoredViewport) ||
    !isNullableObject(data.visualizationData) ||
    (data.riskInventoryRevision !== null &&
      typeof data.riskInventoryRevision !== "string")
  ) {
    throw new Error(
      "Invalid map workspace descriptor: map data snapshots must be objects or null.",
    );
  }
  if (typeof data.propertyForestContextLoading !== "boolean") {
    throw new Error(
      "Invalid map workspace descriptor: map data loading state must be boolean.",
    );
  }
  if (typeof data.onViewportChange !== "function") {
    throw new Error(
      "Invalid map workspace descriptor: map data must expose viewport persistence.",
    );
  }
}

function validateMapDataCoherence(
  descriptor: FmMapWorkspaceDescriptor,
  data: MapWorkspaceData,
): void {
  if (data.activePropertyId !== descriptor.propertyId) {
    throw new Error(
      "Invalid map workspace descriptor: map data property identity must match the workspace.",
    );
  }
  if (
    data.propertyForestContext &&
    (data.propertyForestContext.propertyId !== descriptor.propertyId ||
      data.propertyForestContext.visualization.inventory_revision !==
        descriptor.viewport.revision)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: map inventory must match the workspace property and revision.",
    );
  }
  if (
    data.visualizationData &&
    (data.visualizationData._propertyId !== descriptor.propertyId ||
      data.visualizationData._inventoryRevision !==
        descriptor.viewport.revision)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: map geometry must match the workspace property and revision.",
    );
  }
  if (
    data.riskOverlay &&
    (data.riskOverlay.propertyId !== descriptor.propertyId ||
      data.riskInventoryRevision !== descriptor.viewport.revision)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: map risk evidence must match the workspace property and revision.",
    );
  }
}

function validateRestoredMapData(
  descriptor: FmMapWorkspaceDescriptor,
  data: MapWorkspaceData,
): void {
  if (
    data.restoredSelection &&
    data.restoredSelection.property_id !== descriptor.propertyId
  ) {
    throw new Error(
      "Invalid map workspace descriptor: restored selection must match the workspace property.",
    );
  }
  if (
    data.restoredViewport &&
    (!Number.isFinite(data.restoredViewport.center_lat) ||
      !Number.isFinite(data.restoredViewport.center_lng) ||
      !Number.isFinite(data.restoredViewport.zoom))
  ) {
    throw new Error(
      "Invalid map workspace descriptor: restored viewport coordinates must be finite.",
    );
  }
}

function validateMapData(descriptor: FmMapWorkspaceDescriptor): void {
  if (!descriptor.mapData) return;
  validateMapDataTypes(descriptor.mapData);
  validateMapDataCoherence(descriptor, descriptor.mapData);
  validateRestoredMapData(descriptor, descriptor.mapData);
}

type MapOrientation = NonNullable<FmMapWorkspaceDescriptor["orientation"]>;

function validateOrientationAvailabilityReasons(
  orientation: MapOrientation,
): void {
  for (const [key, availability] of Object.entries(orientation)) {
    if (
      availability &&
      typeof availability === "object" &&
      "status" in availability &&
      availability.status === "unavailable"
    ) {
      requireText(availability.reason, `orientation.${key}.reason`);
    }
  }
}

function validateOrientationArea(orientation: MapOrientation): void {
  if (
    orientation.area.status === "available" &&
    (orientation.area.value.unit !== "ha" ||
      !Number.isFinite(orientation.area.value.value) ||
      orientation.area.value.value < 0)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: orientation.area must be a finite non-negative ha value.",
    );
  }
}

function validateOrientationCounts(orientation: MapOrientation): void {
  for (const [key, count] of [
    ["standCount", orientation.standCount],
    ["matureStandCount", orientation.matureStandCount],
  ] as const) {
    if (
      count.status === "available" &&
      (!Number.isInteger(count.value) || count.value < 0)
    ) {
      throw new Error(
        `Invalid map workspace descriptor: orientation.${key} must be a non-negative integer.`,
      );
    }
  }
}

function validateOrientationText(orientation: MapOrientation): void {
  for (const [key, text] of [
    ["dominantSpecies", orientation.dominantSpecies],
    ["observedOn", orientation.observedOn],
    ["growthCalculatedTo", orientation.growthCalculatedTo],
    ["source", orientation.source],
  ] as const) {
    if (text.status === "available") {
      requireText(text.value, `orientation.${key}.value`);
    }
  }
}

function validateOrientationVolume(orientation: MapOrientation): void {
  if (
    orientation.totalVolume.status === "available" &&
    (orientation.totalVolume.value.unit !== "m3" ||
      !Number.isFinite(orientation.totalVolume.value.value) ||
      orientation.totalVolume.value.value < 0)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: orientation.totalVolume must be a finite non-negative m3 value.",
    );
  }
  if (
    orientation.volumeRange.status === "available" &&
    (orientation.volumeRange.value.unit !== "m3" ||
      !Number.isFinite(orientation.volumeRange.value.low) ||
      !Number.isFinite(orientation.volumeRange.value.high) ||
      orientation.volumeRange.value.low < 0 ||
      orientation.volumeRange.value.high < orientation.volumeRange.value.low)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: orientation.volumeRange must be an ordered finite non-negative m3 range.",
    );
  }
}

function validateOrientation(orientation: MapOrientation): void {
  requireText(orientation.ariaLabel, "orientation.ariaLabel");
  requireText(orientation.propertyLabel, "orientation.propertyLabel");
  validateOrientationAvailabilityReasons(orientation);
  validateOrientationArea(orientation);
  validateOrientationCounts(orientation);
  validateOrientationText(orientation);
  validateOrientationVolume(orientation);
}

function validateRadar(descriptor: FmMapWorkspaceDescriptor): void {
  if (descriptor.radar.propertyId !== descriptor.propertyId) {
    throw new Error(
      "Invalid map workspace descriptor: radar property identity must match the workspace.",
    );
  }
  descriptor.radar.frames.forEach((frame, index) => {
    requireText(frame.id, `radar.frames[${index}].id`);
    requireText(frame.capturedAt, `radar.frames[${index}].capturedAt`);
    requireText(frame.grid.crs, `radar.frames[${index}].grid.crs`);
    if (
      frame.grid.bbox.length !== 4 ||
      frame.grid.bbox.some((value) => !Number.isFinite(value))
    ) {
      throw new Error(
        `Invalid map workspace descriptor: radar.frames[${index}].grid.bbox must contain four finite coordinates.`,
      );
    }
  });
  Object.entries(descriptor.radar.copy).forEach(([key, value]) =>
    requireText(value, `radar.copy.${key}`),
  );
}

function validateRiskLayerAccess(
  descriptor: FmMapWorkspaceDescriptor,
  actionIds: Set<string>,
): void {
  descriptor.layers.forEach((layer) => {
    if (layer.kind !== "risk") return;
    if (layer.access.kind === "available")
      requireText(layer.access.advisory, `layer "${layer.id}".access.advisory`);
    if (layer.access.kind === "loading")
      requireText(layer.access.label, `layer "${layer.id}".access.label`);
    if (layer.access.kind === "error")
      requireText(layer.access.message, `layer "${layer.id}".access.message`);
    if (layer.access.kind === "unavailable")
      requireText(layer.access.reason, `layer "${layer.id}".access.reason`);
    if (layer.access.kind === "not-evaluable") {
      requireText(layer.access.reason, `layer "${layer.id}".access.reason`);
      if (!Number.isInteger(layer.access.unwatchedStandCount) || layer.access.unwatchedStandCount <= 0)
        throw new Error(`Invalid map workspace descriptor: layer "${layer.id}".access.unwatchedStandCount must be a positive integer.`);
    }
    if (layer.access.kind === "locked") {
      for (const key of [
        "title",
        "explanation",
        "actionId",
        "actionLabel",
      ] as const) {
        requireText(layer.access[key], `layer "${layer.id}".access.${key}`);
      }
      if (!actionIds.has(layer.access.actionId)) {
        throw new Error(
          `Invalid map workspace descriptor: locked layer "${layer.id}" action must reference one registered action.`,
        );
      }
    }
  });
}

function validateMapOwnedFormFlow(
  dialog: Extract<
    NonNullable<FmMapWorkspaceDescriptor["dialogs"]>[number],
    { kind: "form-flow" }
  >,
): void {
  const nestedMap = dialog.flow.blocks.find(
    ({ control }) => control.type === "map",
  );
  if (nestedMap) {
    throw new Error(
      `Invalid map workspace descriptor: form-flow dialog "${dialog.id}" block "${nestedMap.id}" cannot mount a second map; publish its resolved spatial context to the parent map workspace.`,
    );
  }
}

function validateMapWorkspaceDialogs(
  descriptor: FmMapWorkspaceDescriptor,
): void {
  uniqueIds(descriptor.dialogs ?? [], "dialogs");
  (descriptor.dialogs ?? []).forEach((dialog) => {
    requireText(dialog.title, `dialog "${dialog.id}".title`);
    requireText(dialog.closeLabel, `dialog "${dialog.id}".closeLabel`);
    if (dialog.kind === "form-flow") {
      const closeConfirmation = dialog.closeConfirmation;
      if (closeConfirmation) {
        (["title", "description", "confirmLabel", "cancelLabel"] as const)
          .forEach((field) => requireText(
            closeConfirmation[field], `dialog "${dialog.id}".closeConfirmation.${field}`,
          ));
      }
      assertFmFormFlowPageDescriptor(dialog.flow);
      validateMapOwnedFormFlow(dialog);
    } else if (dialog.kind === "analysis-detail") {
      validateAnalysisWorkspaceDescriptor(dialog.workspace);
    } else {
      // Its regions validate their own sections inside their error boundaries.
      assertFmOperationalWorkspaceShell(dialog.workspace);
    }
  });
}

function validateMapWorkspaceStateActions(
  descriptor: FmMapWorkspaceDescriptor,
  actionIds: ReadonlySet<string>,
): void {
  if (
    descriptor.state.recoveryActionId
    && !actionIds.has(descriptor.state.recoveryActionId)
  ) {
    throw new Error(
      "Invalid map workspace descriptor: state recoveryActionId must reference one action.",
    );
  }
  if (descriptor.mode === "orient" && !descriptor.orientation) {
    throw new Error(
      "Invalid map workspace descriptor: Orient mode requires the novice orientation answer.",
    );
  }
  if (descriptor.orientation) validateOrientation(descriptor.orientation);
}

export function validateMapWorkspaceDescriptor(
  descriptor: FmMapWorkspaceDescriptor,
): void {
  validateMapWorkspaceShape(descriptor);
  requireText(descriptor.id, "id");
  if (descriptor.propertyId !== null)
    requireText(descriptor.propertyId, "propertyId");
  requireText(descriptor.modeLabel, "modeLabel");
  validateStory(descriptor);
  requireText(descriptor.viewport.id, "viewport.id");
  requireText(descriptor.viewport.revision, "viewport.revision");
  requireText(descriptor.source.id, "source.id");
  requireText(descriptor.source.label, "source.label");
  requireText(descriptor.state.label, "state.label");
  if (!MAP_WORKSPACE_STATES.includes(descriptor.state.kind)) {
    throw new Error(
      "Invalid map workspace descriptor: state.kind must be ready, loading, empty, partial, stale, locked, or error.",
    );
  }
  requireText(descriptor.copy.rendererUnavailable, "copy.rendererUnavailable");
  requireText(
    descriptor.copy.appliedFiltersAriaLabel,
    "copy.appliedFiltersAriaLabel",
  );
  if (!hasMapSlot(descriptor.mapSlotKey)) {
    throw new Error(
      `Invalid map workspace descriptor: mapSlotKey "${descriptor.mapSlotKey}" is not governed.`,
    );
  }
  const geometryRefs = new Set(descriptor.geometryRefs);
  descriptor.geometryRefs.forEach((geometryRef) =>
    requireText(geometryRef, "geometryRefs"),
  );
  if (geometryRefs.size !== descriptor.geometryRefs.length) {
    throw new Error(
      "Invalid map workspace descriptor: geometryRefs must be unique stable references.",
    );
  }
  const baseMapIds = uniqueIds(descriptor.baseMaps, "baseMaps");
  descriptor.baseMaps.forEach((baseMap) =>
    requireText(baseMap.label, `baseMap "${baseMap.id}".label`),
  );
  if (!baseMapIds.has(descriptor.activeBaseMapId)) {
    throw new Error(
      "Invalid map workspace descriptor: activeBaseMapId must reference one registered base map.",
    );
  }
  uniqueIds(descriptor.features, "features");
  descriptor.features.forEach((feature) => {
    requireText(feature.label, `feature "${feature.id}".label`);
    if (feature.geometryRef !== null) {
      requireText(feature.geometryRef, `feature "${feature.id}".geometryRef`);
    }
    if (feature.sourceId !== descriptor.source.id) {
      throw new Error(
        `Invalid map workspace descriptor: feature "${feature.id}" must use the immutable workspace source id.`,
      );
    }
    if (
      feature.geometryRef !== null
      && !geometryRefs.has(feature.geometryRef)
    ) {
      throw new Error(
        `Invalid map workspace descriptor: feature "${feature.id}" references unknown geometry.`,
      );
    }
    if (feature.relatedCampaignIds !== undefined) {
      requireTextArray(
        feature.relatedCampaignIds,
        `feature "${feature.id}".relatedCampaignIds`,
      );
    }
  });
  const layerIds = uniqueIds(descriptor.layers, "layers");
  descriptor.layers.forEach((layer) => {
    const capability = FM_MAP_LAYER_CAPABILITIES[layer.id];
    if (!capability || capability.kind !== layer.kind) {
      throw new Error(
        `Invalid map workspace descriptor: layer "${layer.id}" must match its sealed capability kind.`,
      );
    }
    if (layer.sourceId !== descriptor.source.id) {
      throw new Error(
        `Invalid map workspace descriptor: layer "${layer.id}" must use the immutable workspace source id.`,
      );
    }
  });
  validateRadar(descriptor);
  const filterIds = uniqueIds(descriptor.filters, "filters");
  descriptor.filters.forEach((filter) => {
    requireText(filter.sourceId, `filter "${filter.id}".sourceId`);
    if (filter.sourceId !== descriptor.source.id) {
      throw new Error(
        `Invalid map workspace descriptor: filter "${filter.id}" must use the immutable workspace source id.`,
      );
    }
    const optionValues = new Set(filter.options.map((option) => option.value));
    filter.options.forEach((option) => {
      requireText(option.value, `filter "${filter.id}".option.value`);
      requireText(option.label, `filter "${filter.id}".option.label`);
    });
    if (filter.selectedValues.some((value) => !optionValues.has(value))) {
      throw new Error(
        `Invalid map workspace descriptor: filter "${filter.id}" selects an unknown semantic value.`,
      );
    }
  });
  validateMapWorkspacePanels(descriptor, filterIds, layerIds);
  validateMapWorkspaceSelection(descriptor);
  validateMapWorkspaceInspection(descriptor);
  validateMapData(descriptor);
  const expectedSelections =
    MAP_WORKSPACE_MODE_IDENTITY[descriptor.mode].selectionKinds;
  if (!expectedSelections.some((kind) => kind === descriptor.selection.kind)) {
    throw new Error(
      `Invalid map workspace descriptor: ${descriptor.mode} mode does not support ${descriptor.selection.kind} selection.`,
    );
  }
  const actionIds = uniqueIds(descriptor.actions, "actions");
  descriptor.actions.forEach((action) =>
    requireText(action.label, `action "${action.id}".label`),
  );
  validateRiskLayerAccess(descriptor, actionIds);
  validateMapWorkspaceDialogs(descriptor);
  validateMapWorkspaceStateActions(descriptor, actionIds);
}
